/**
 * Centralized API endpoints configuration
 * Based on the official API documentation
 */

export const API_ENDPOINTS = {
  // Base configuration
  BASE_URL: "/api",
  PRODUCTS: {
    PREVIEW: "/products/preview",
  },

  // Authentication endpoints
  AUTH: {
    SIGNUP: "/auth/signup",
    LOGIN: "/auth/login",
    LOGOUT: "/auth/logout",
    ME: "/auth/me",
    CHECK: "/auth/me", // il backend non ha /auth/check: il "check" È /auth/me
  },

  // SSO endpoints
  SSO: {
    CALLBACK: "/sso/callback",
    LOGOUT: "/sso/logout",
    ME: "/sso/me",
  },

  // Bot management endpoints
  BOTS: {
    LIST: "/bots",
    CREATE: "/bots",
    GET: (botId) => `/bots/${botId}`,
    UPDATE: (botId) => `/bots/${botId}`,
    DELETE: (botId) => `/bots/${botId}`,
    WIDGET_CONFIG: (botId) => `/bots/${botId}/widget-config`,
    WIDGET_EMBED: (botId) => `/bots/${botId}/widget-embed`,
  },

  // Source management endpoints
  SOURCES: {
    LIST: (botId) => `/sources/${botId}`,
    LIST_ALL: (botId) => `/sources/${botId}/all`, // For compatibility - all sources without pagination
    CREATE: (botId) => `/sources/${botId}`,
    BULK_CREATE: (botId) => `/sources/${botId}/bulk`, // Bulk create multiple sources
    BULK_DELETE: (botId) => `/sources/${botId}/bulk-delete`, // Bulk delete sources
    BULK_REINGEST: (botId) => `/sources/${botId}/bulk-reingest`, // Bulk re-ingest (ids or whole filter)
    GET: (botId, sourceId) => `/sources/${botId}/${sourceId}`, // This might not exist in real API
    DELETE: (sourceId) => `/sources/${sourceId}`,
    UPDATE: (sourceId) => `/sources/${sourceId}`,
    REINGEST: (sourceId) => `/sources/${sourceId}/reingest`,
    SCHEDULE_GET: (sourceId) => `/sources/${sourceId}/schedule`,
    SCHEDULE_UPDATE: (sourceId) => `/sources/${sourceId}/schedule`,
    VIEW: (sourceId) => `/sources/${sourceId}/view`, // Signed URL to open a document

    // New endpoints for pagination and sitemap expansion
    SITEMAP_CHILDREN: (botId, sitemapId) => `/sources/${botId}/sitemap/${sitemapId}/children`,
    SITEMAP_STATS: (botId, sitemapId) => `/sources/${botId}/sitemap/${sitemapId}/stats`,
  },

  // Upload endpoints
  UPLOADS: {
    GENERATE_URL: "/uploads/generate-url",
    STATUS: (blobName) => `/uploads/status/${blobName}`,
    WEBHOOK: "/uploads/webhook",
  },

  // Query endpoints
  QUERY: {
    ASK: "/query", // Single endpoint for all queries
  },

  // Conversation endpoints
  CONVERSATIONS: {
    LIST: (botId) => `/conversations/${botId}`,
    GET: (botId, sessionId) => `/conversations/${botId}/${sessionId}`,
    DELETE: (botId, sessionId) => `/conversations/${botId}/${sessionId}`,
    UPDATE: (botId, sessionId) => `/conversations/${botId}/${sessionId}`,
    STATS: (botId) => `/conversations/${botId}/stats`,
    EXPORT: (botId) => `/conversations/${botId}/export`,
  },

  // Contacts endpoints (modulo contatti in chat)
  CONTACTS: {
    LIST: (botId) => `/contacts/${botId}`,
    STATS: (botId) => `/contacts/${botId}/stats`,
    EXPORT: (botId) => `/contacts/${botId}/export`,
    FORM: (botId) => `/contacts/${botId}/form`,
    FORM_DRAFT: (botId) => `/contacts/${botId}/form/draft`,
    UPDATE: (botId, contactId) => `/contacts/${botId}/${contactId}`,
    DELETE: (botId, contactId) => `/contacts/${botId}/${contactId}`,
    FILE: (botId, contactId, uploadId) => `/contacts/${botId}/${contactId}/files/${uploadId}`,
  },

  // Apps & Integrazioni di un bot
  INTEGRATIONS: (botId) => `/bots/${botId}/integrations`,

  // Analytics endpoints
  ANALYTICS: {
    BOT: (botId) => `/analytics/bot/${botId}`,
    BOT_TIMESERIES: (botId) => `/analytics/bot/${botId}/timeseries`,
    TOP_CITATIONS: (botId) => `/analytics/bot/${botId}/top-citations`,
    TOP_CARDS: (botId) => `/analytics/bot/${botId}/top-cards`,
    TENANT: "/analytics/tenant",
  },

  // Billing endpoints
  BILLING: {
    CATALOG: "/billing/catalog",
    SUBSCRIPTION: "/billing/subscription",
    DOMAINS: "/billing/domains",
    PROFILE: "/billing/profile",
    ACCOUNT: "/billing/account",
    CHECKOUT: "/billing/checkout",
    CHECKOUT_CONFIRM: "/billing/checkout/conferma",
    PORTAL: "/billing/portal",
    SYNC: "/billing/sincronizza",
    SCHEDULED_CANCEL: "/billing/cambio-programmato/annulla",
  },

  // Health check endpoints
  HEALTH: {
    BASIC: "/health",
    DETAILED: "/health/detailed",
    READY: "/health/ready",
    LIVE: "/health/live",
  },
};

/**
 * Helper function to build URLs with parameters
 */
export const buildUrl = (endpoint, params = {}) => {
  if (typeof endpoint === "function") {
    console.error(endpoint(...Object.values(params)));
    return endpoint(...Object.values(params));
  }
  return endpoint;
};

/**
 * Validate that all required parameters are provided
 */
export const validateEndpointParams = (
  endpoint,
  params,
  requiredParams = []
) => {
  const missing = requiredParams.filter(
    (param) => params[param] === undefined || params[param] === null
  );

  if (missing.length > 0) {
    throw new Error(
      `Missing required parameters for ${endpoint}: ${missing.join(", ")}`
    );
  }
};
